import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  DeviceEventEmitter,
  Platform,
  TouchableOpacity,
  View,
} from "react-native";
import { WebView } from "react-native-webview";
import styled from "styled-components/native";
import { WebviewProps } from "./types";
import { useTheme } from "styled-components/native";
import Loader from "../Loader";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { SpaceType } from "../Spacer/types";
import { useSelector } from "react-redux";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { DefaultTheme } from "styled-components/native";
import { useWebViewNativeLifecycle } from "../../../hooks/useWebViewNativeLifecycle";

const ThreeDWebview = ({
  url = "",
  height,
  width,
  isSmallDeviceWidth,
  injectedJavascript = "",
  injectedCSS = "",
  deviceWidth = 0,
  resizeImage = false,
  extraSmallImage = false,
  isFromPDP = true,
  errorCallback,
  isClickable = true,
  allowScaleScript = true,
  showLoader = false,
  mediaPlaybackRequiresUserAction = false,
  injectJavaScript = {},
  iframeKey = "",
  threeDProperties,
  setThreeDProperties,
}: WebviewProps) => {
  const theme = useTheme();
  const webviewRef = useRef<WebView>(null);
  const { isMountedRef, keepNativeSurface } =
    useWebViewNativeLifecycle(webviewRef);
  const loadEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { initStatus } = useOrgSettingsSlice();
  const { applyPersonalizationEngravedText } = useJewelryConfiguratorState();
  const [threeDLoader, setThreeDLoader] = useState(false);
  const three_d_settings = useSelector(
    (state: any) => state.globalData?.app_settings?.three_d
  );

  const calculatedHeight =
    height ??
    (isSmallDeviceWidth
      ? extraSmallImage
        ? deviceWidth
        : resizeImage
        ? isFromPDP
          ? deviceWidth / 1.5
          : "100%"
        : deviceWidth
      : "100%");
  // const [loading, setLoading] = useState(true);
  const [isFullScreenVisible, setIsFullScreenVisible] = useState(false);

  const handleLoadEnd = () => {
    if (loadEndTimerRef.current) {
      clearTimeout(loadEndTimerRef.current);
    }
    loadEndTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      webviewRef.current?.postMessage(JSON.stringify(injectJavaScript));
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (loadEndTimerRef.current) {
        clearTimeout(loadEndTimerRef.current);
      }
    };
  }, []);

  const handleOnMessage = (event: any) => {
    if (!isMountedRef.current) return;
    const data = typeof event === "string" ? JSON.parse(event) : event;
    if (data.type === "THREE_D_LOADED") {
      if (
        threeDProperties &&
        typeof threeDProperties === "object" &&
        Object.keys(threeDProperties).length !== 0
      ) {
        Object.keys(threeDProperties).forEach((key) => {
          switch (key) {
            case "autoRotate":
              const autoRotateScript = {
                three_d_data_type:
                  initStatus?.three_d_settings?.three_d_data_type ?? "webgi",
                functionName: "setAutoRotate",
                params: { enabled: threeDProperties.autoRotate },
              };
              // @ts-ignore
              if (Platform.OS !== "web") {
                webviewRef.current?.postMessage(
                  JSON.stringify(autoRotateScript)
                );
              } else {
                const iframe = document.getElementById(iframeKey);
                // @ts-ignore
                if (iframe)
                  (iframe as any).contentWindow?.postMessage(
                    autoRotateScript,
                    "*"
                  );
              }
              break;
            default:
              break;
          }
        });
      }
      applyPersonalizationEngravedText();
    } else if (data.type === "CAPTURE_COMPLETED") {
      setThreeDLoader(false);
    }
  };

  useEffect(() => {
    if (Platform.OS === "web") {
      const handleIframeMessage = (event: MessageEvent) => {
        const iframe = document.getElementById(iframeKey) as HTMLIFrameElement;
        if (iframe && event.source === iframe.contentWindow) {
          try {
            const data =
              typeof event.data === "string"
                ? JSON.parse(event.data)
                : event.data;
            handleOnMessage(data);
          } catch (error) {
            console.error("Error parsing iframe message:", error);
          }
        }
      };

      // Add message event listener
      window.addEventListener("message", handleIframeMessage);

      // Cleanup listener on unmount
      return () => {
        window.removeEventListener("message", handleIframeMessage);
      };
    }
  }, [iframeKey]);

  const handleNavigationStateChange = () => {
    // if (!navState.loading && navState.url && navState.url !== "about:blank") {
    //   handleLoadEnd();
    // }
  };

  const calculatedWidth =
    width ??
    (isSmallDeviceWidth && theme.isMobile
      ? extraSmallImage
        ? deviceWidth
        : resizeImage
        ? isFromPDP
          ? deviceWidth / 1.5
          : 250
        : deviceWidth
      : // Fix for external players ...
        "100%");

  const handleNavigation = () => {
    return true;
  };

  const injectionScript = `
  if(${allowScaleScript}){
    (function() {
      var meta = document.createElement('meta');
      meta.setAttribute('name', 'viewport');
      meta.setAttribute('content', 'width=device-width, initial-scale=1');
      document.getElementsByTagName('head')[0].appendChild(meta);
    })();
  }
  

  (function() {
    var style = document.createElement('style');
    style.textContent = ${JSON.stringify(injectedCSS)};
    document.head.appendChild(style);

    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.textContent = ${JSON.stringify(injectedJavascript)};
    document.documentElement.appendChild(script);

    var pointerScript = document.createElement('script');
    pointerScript.type = 'text/javascript';
    var pointerEventsValue = ${
      iframeKey === "vdb-sticky-iframe"
        ? '"none"'
        : isClickable
        ? '"auto"'
        : '"none"'
    };
     pointerScript.textContent = 'document.body.style.pointerEvents = "' + pointerEventsValue + '"; document.documentElement.style.pointerEvents = "' + pointerEventsValue + '"; document.body.style.userSelect = "none"; document.body.style.webkitUserSelect = "none";document.body.style.webkitTouchCallout = "none"; document.body.style.msUserSelect = "none"; document.body.style.overflow = "hidden";';
    document.documentElement.appendChild(pointerScript);

    // Additional prevention for vdb-sticky-iframe
    ${
      iframeKey === "vdb-sticky-iframe"
        ? `
    var preventScript = document.createElement('script');
    preventScript.type = 'text/javascript';
    preventScript.textContent = 'document.addEventListener("wheel", function(e) { e.preventDefault(); }, { passive: false }); document.addEventListener("touchmove", function(e) { e.preventDefault(); }, { passive: false }); document.addEventListener("scroll", function(e) { e.preventDefault(); }, { passive: false }); document.addEventListener("click", function(e) { e.preventDefault(); e.stopPropagation(); }, true); document.addEventListener("mousedown", function(e) { e.preventDefault(); }, true); document.addEventListener("mouseup", function(e) { e.preventDefault(); }, true); document.addEventListener("contextmenu", function(e) { e.preventDefault(); }, true);';
    document.documentElement.appendChild(preventScript);`
        : ""
    }
  })();

  true;
`;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (Platform.OS === "web") {
      const iframe = document.getElementById(iframeKey) as HTMLIFrameElement;
      if (
        iframe &&
        injectJavaScript &&
        typeof injectJavaScript === "object" &&
        Object.keys(injectJavaScript).length !== 0
      ) {
        iframe.onload = () => {
          setTimeout(() => {
            iframe.contentWindow?.postMessage(injectJavaScript, "*");
          }, 200);
        };
      }
    }
  }, [injectJavaScript, iframeKey]);

  const handleWebViewError = (syntheticEvent: any) => {
    if (!isMountedRef.current) return;
    const { nativeEvent } = syntheticEvent;
    if (errorCallback) {
      errorCallback(nativeEvent);
    }
  };

  const handleContentProcessTerminate = useCallback(() => {
    console.warn("WebView process terminated, reloading...");
  }, []);

  const handleRenderProcessGone = useCallback((event: any) => {
    console.warn(
      "WebView render process gone, reloading...",
      event.nativeEvent
    );
  }, []);

  const showFullScreen = (_iframeKey: string) => {
    const iframeElement = document.getElementById(_iframeKey);
    const parentDivElement = iframeElement?.parentElement;
    if (parentDivElement && !document.fullscreenElement) {
      let request;
      if (parentDivElement.requestFullscreen) {
        request = parentDivElement.requestFullscreen();
        // @ts-check
      } else if ((parentDivElement as any).webkitRequestFullscreen) {
        request = (parentDivElement as any).webkitRequestFullscreen(); // Safari
      } else if ((parentDivElement as any).msRequestFullscreen) {
        request = (parentDivElement as any).msRequestFullscreen(); // IE11
      }
      if (request) {
        request
          .then(() => {
            console.log("Fullscreen entered");
            setIsFullScreenVisible(true);
          })
          .catch((err: any) => {
            console.error("Fullscreen request failed:", err);
          });
      }
    }
  };

  const closeFullScreen = (_iframeKey: string) => {
    const iframeElement = document.getElementById(_iframeKey);
    const parentDivElement = iframeElement?.parentElement;
    if (parentDivElement && document.fullscreenElement) {
      let request;
      if (document.exitFullscreen) {
        request = document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        request = (document as any).webkitExitFullscreen(); // Safari
      } else if ((document as any).msExitFullscreen) {
        request = (document as any).msExitFullscreen(); // IE11
      }
      if (request) {
        request
          .then(() => {
            console.log("Fullscreen exited");
            setIsFullScreenVisible(false);
          })
          .catch((err: any) => {
            console.error("Fullscreen request failed:", err);
          });
      }
    }
  };

  useEffect(() => {
    if (Platform.OS === "web") {
      const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Escape" && document.fullscreenElement) {
          closeFullScreen(iframeKey);
        }
      };

      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          setIsFullScreenVisible(false);
        }
      };

      document.addEventListener("keydown", handleKeyPress);
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      ); // Safari
      document.addEventListener("msfullscreenchange", handleFullscreenChange); // IE11

      return () => {
        document.removeEventListener("keydown", handleKeyPress);
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "msfullscreenchange",
          handleFullscreenChange
        );
      };
    }
  }, [iframeKey]);

  useEffect(() => {
    const threeDObject = DeviceEventEmitter.addListener(
      "THREE_D_WEBVIEW",
      (_data) => {
        const { iframeKey: keys, data } = _data;
        if (!keys || !keys?.length || !keys.includes(iframeKey)) return;
        if (Platform.OS === "web") {
          if (data.functionName === "showFullScreen") {
            showFullScreen(iframeKey);
            return;
          } else if (data.functionName === "closeFullScreen") {
            closeFullScreen(iframeKey);
            return;
          } else if (data.functionName === "captureCanvas") {
            setThreeDLoader(true);
          }
          const iframe = document.getElementById(iframeKey);
          // @ts-ignore
          if (iframe) iframe?.contentWindow?.postMessage(data, "*");
        } else {
          if (!isMountedRef.current) return;
          webviewRef.current?.postMessage(JSON.stringify(data));
        }
      }
    );

    return () => {
      threeDObject.remove();
    };
  }, [iframeKey]);

  const rotate3D = () => {
    DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
      iframeKey: [
        "vdb-gallery-iframe",
        "vdb-sticky-iframe",
        "vdb-overlay-iframe",
      ],
      data: {
        three_d_data_type:
          initStatus?.three_d_settings?.three_d_data_type ?? "webgi",
        functionName: "setAutoRotate",
        params: { enabled: !threeDProperties?.autoRotate },
      },
    });
    setThreeDProperties?.((prevState: any) => ({
      ...prevState,
      autoRotate: !prevState?.autoRotate,
    }));
  };

  const captureCanvas = () => {
    setThreeDLoader(true);
    DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
      iframeKey: [iframeKey],
      data: {
        three_d_data_type:
          initStatus?.three_d_settings?.three_d_data_type ?? "webgi",
        functionName: "captureCanvas",
        params: {},
      },
    });
  };

  return (
    <Container style={{ pointerEvents: isClickable ? "auto" : "none" }}>
      {showLoader && <Loader />}
      {Platform.OS === "web" && iframeKey ? (
        <View style={{ flex: 1, width: "100%", height: "100%" }}>
          {threeDLoader && (
            <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
          )}
          <iframe
            ref={iframeRef}
            id={iframeKey}
            key={`${iframeKey}`}
            style={{
              height: calculatedHeight,
              width: calculatedWidth,
              flex: 1,
              borderWidth: 0,
              pointerEvents:
                iframeKey === "vdb-sticky-iframe" ? "none" : "auto",
            }}
            scrolling="no"
            // @ts-ignore
            src={url}
            className="w-full h-96 border border-gray-200 rounded"
            onLoad={() => {
              handleLoadEnd();
            }}
          />
          {Platform.OS === "web" && theme.isLargeScreen ? (
            isFullScreenVisible ? (
              <>
                <button
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 1000000000,
                    border: "none",
                    background: "transparent",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                  onClick={() => closeFullScreen(iframeKey)}
                >
                  <VDBIcon
                    name={VDBIcons.CLOSE_CIRCLE}
                    size={24}
                    color={theme["core-06"]}
                    variant={IconsVariant.VDBCustom}
                  />
                </button>
                <ButtonsParentContainer>
                  <TouchableOpacity onPress={rotate3D} activeOpacity={0.8}>
                    <IconContainer>
                      <VDBIcon
                        name={
                          threeDProperties?.autoRotate
                            ? VDBIcons.THREE_D_PAUSE
                            : VDBIcons.THREE_D_PLAY
                        }
                        size={24}
                        color={theme["core-06"]}
                        variant={IconsVariant.VDBCustom}
                      />
                    </IconContainer>
                  </TouchableOpacity>
                  {Platform.OS === "web" &&
                    (three_d_settings?.showScreenshotIcon ?? false) && (
                      <TouchableOpacity
                        onPress={captureCanvas}
                        activeOpacity={0.8}
                      >
                        <IconContainer>
                          <VDBIcon
                            name={VDBIcons.SCREENSHOT}
                            size={24}
                            color={theme["core-06"]}
                            variant={IconsVariant.VDBCustom}
                          />
                        </IconContainer>
                      </TouchableOpacity>
                    )}
                </ButtonsParentContainer>
              </>
            ) : (
              <button
                style={{
                  position: "absolute",
                  bottom: 10,
                  right: 10,
                  zIndex: 1000000000,
                  border: "none",
                  background: "transparent",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
                onClick={() => showFullScreen(iframeKey)}
              >
                <VDBIcon
                  name={VDBIcons.EXPAND}
                  size={15}
                  color={theme["core-06"]}
                  variant={IconsVariant.VDBCustom}
                />
              </button>
            )
          ) : null}
        </View>
      ) : keepNativeSurface ? (
        <WebView
          ref={webviewRef}
          key={iframeKey}
          // @ts-ignore
          source={{ uri: url }}
          style={{
            height: calculatedHeight,
            width: calculatedWidth,
            flex: 1,
            // @ts-ignore
            resizeMode: "cover",
            pointerEvents: isClickable ? "auto" : "none",
          }}
          {...(Platform.OS === "ios" && { decelerationRate: "normal" })}
          {...(Platform.OS === "android" && {
            androidLayerType: "none",
          })}
          onMessage={(event) => handleOnMessage(event?.nativeEvent?.data)}
          injectedJavaScript={injectionScript}
          injectJavaScript={JSON.stringify(injectJavaScript)}
          javaScriptEnabled={true}
          scalesPageToFit={Platform.OS === "ios"}
          domStorageEnabled={true}
          allowsInlineMediaPlayback={true}
          mediaPlaybackRequiresUserAction={mediaPlaybackRequiresUserAction}
          automaticallyAdjustContentInsets={false}
          onShouldStartLoadWithRequest={handleNavigation}
          onError={handleWebViewError}
          onHttpError={handleWebViewError}
          onLoadStart={() => {
            // setLoading(true);
          }}
          onLoadEnd={() => {
            handleLoadEnd();
          }}
          onNavigationStateChange={handleNavigationStateChange}
          onContentProcessDidTerminate={handleContentProcessTerminate}
          onRenderProcessGone={handleRenderProcessGone}
        />
      ) : (
        <View
          style={{
            height: calculatedHeight,
            width: calculatedWidth,
            flex: 1,
          }}
        />
      )}
    </Container>
  );
};

const Container = styled(View)`
  flex: 1;
`;

const ButtonsParentContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 50%;
  right: 10;
  z-index: 1000000000;
`;

const IconContainer = styled.View<any>`
  padding: ${SpaceType.Basic}px;
`;

export default React.memo(ThreeDWebview, (prevProps, nextProps) => {
  return (
    prevProps.url === nextProps.url &&
    prevProps.height === nextProps.height &&
    prevProps.width === nextProps.width &&
    prevProps.isSmallDeviceWidth === nextProps.isSmallDeviceWidth &&
    prevProps.deviceWidth === nextProps.deviceWidth &&
    prevProps.resizeImage === nextProps.resizeImage &&
    prevProps.extraSmallImage === nextProps.extraSmallImage &&
    prevProps.isFromPDP === nextProps.isFromPDP &&
    prevProps.isClickable === nextProps.isClickable &&
    prevProps.allowScaleScript === nextProps.allowScaleScript &&
    prevProps.showLoader === nextProps.showLoader &&
    prevProps.mediaPlaybackRequiresUserAction ===
      nextProps.mediaPlaybackRequiresUserAction &&
    prevProps.iframeKey === nextProps.iframeKey &&
    JSON.stringify(prevProps.injectJavaScript) ===
      JSON.stringify(nextProps.injectJavaScript) &&
    JSON.stringify(prevProps.threeDProperties) ===
      JSON.stringify(nextProps.threeDProperties)
  );
});
